/* @flow */
// typecheck: ignore file
import React from 'react';
import invariant from 'invariant';

import BoxBody from 'components/BoxBody.jsx';
import Button from 'components/Button.jsx';
import Buttons from 'components/Buttons.jsx';
import Checkbox from 'components/Checkbox.jsx';
import Copy from 'components/Copy.jsx';
import Dialog from 'components/Dialog.jsx';
import Field from 'components/Field.jsx';
import Label from 'components/Label.jsx';
import Link from 'components/Link.jsx';
import LinkConstants from 'constants/LinkConstants';
import ModalTrigger from 'components/ModalTrigger.jsx';
import OrganizationActions from 'actions/OrganizationActions';
import Textarea from 'components/Textarea.jsx';
import ToastActions from 'actions/ToastActions';
import Group from 'entities/Group';

type Props = {
  reportModal?: Function,
  userName?: string,
  userIds: number[],
  orgName: string,
  orgId: number,
  group?: Group,
  isAdminView?: bool,
  onRequestHide?: Function,
  onRemoved?: Function,
};

class RemoveFromOrgModal extends React.Component {
  props: Props;

  state = {
    isSubmitting: false,
    selectedBan: false,
    reason: '',
  };

  closeModal = () => {
    const { onRequestHide } = this.props;
    invariant(onRequestHide, 'modal must have onRequestHide');
    onRequestHide();
  };

  onNoteChange = (event: any) => {
    this.setState({ reason: event.target.value });
  };

  removeUser = () => {
    const { orgName, orgId, userName, userIds, onRemoved } = this.props;

    const isBan = this.state.selectedBan;
    const finishedWord = isBan ? 'banned' : 'removed';

    const onSuccess = () => {
      let peopleText;
      if (userName) {
        peopleText = `${userName} has`;
      } else {
        peopleText = userIds.length === 1 ? '1 person has' : `${userIds.length} people have`;
      }
      this.setState({ isSubmitting: false });
      ToastActions.showSuccessToast(`${peopleText} been ${finishedWord} from ${orgName}`);
      if (onRemoved) {
        onRemoved();
      }
      this.closeModal();
    };

    const onError = () => {
      this.setState({ isSubmitting: false });
      ToastActions.showErrorToast(`Something went wrong! No one was ${finishedWord} from ${orgName}.`);
    };

    this.setState({ isSubmitting: true });

    const removeAction = isBan ? OrganizationActions.batchBanFromOrg : OrganizationActions.batchRemoveFromOrg;
    removeAction(orgId, userIds, this.state.reason).then(onSuccess, onError);
  };

  renderBody = () => {
    const { userName, isAdminView, group, reportModal, userIds } = this.props;
    const { reason, isSubmitting } = this.state;

    let reportLink;
    if ((isAdminView || userIds.length > 1) && group) {
      reportLink = (
        <Link href={`https://www.remind.com/classes/${group.getCode()}/people`} target="_blank">
          report that person to Remind
        </Link>
      );
    } else if (reportModal) {
      reportLink = (
        <ModalTrigger modal={reportModal(userName, userIds[0])}>
          <Link onClick={this.closeModal}>report that person to Remind</Link>
        </ModalTrigger>
      );
    }

    return (
      <BoxBody divided>
        <Copy color="thunder">
          <div>
            {!isAdminView &&
              <p>
                People you remove from {this.props.orgName} will not be able to rejoin the school.  However, they
                keep all account privileges, including access to their classes and participant lists.
              </p>}
            <p>
              If {userName ? `${userName} is` : 'they are'} violating our
              {' '}
              <Link
                href={LinkConstants.COMMUNITY_GUIDELINES}
                target="_blank">
                Community Guidelines
              </Link>
              , please
              {' '}
              {reportLink}.
            </p>
            When you remove {userName || 'them'},
            <ul>
              <li>They will no longer appear in the school roster.</li>
              <li>They will be removed from classes linked to the school.</li>
              <li>Classes they own will be unlinked from the school.</li>
            </ul>
            <p>
              {userName || 'They'} will be notified by email, and you can add a custom message below.
            </p>
          </div>
        </Copy>
        <Field>
          <Label>Type your message</Label>
          <Textarea
            value={reason}
            placeholder="Type your message here"
            onChange={this.onNoteChange}
          />
        </Field>
        <Field>
          <Checkbox
            data-test="ban-checkbox"
            onChange={() => this.setState({ selectedBan: !this.state.selectedBan })}>
            <Copy color="thunder">
              Ban {userName || 'these people'} from joining {this.props.orgName} again
            </Copy>
          </Checkbox>
        </Field>
        <Buttons axis="y">
          <Button
            data-test="confirm-button"
            color="brand"
            level="primary"
            full
            disabled={isSubmitting}
            onClick={this.removeUser}>
            Remove from school
          </Button>
          <Button color="brand" level="text" onClick={this.closeModal}>Cancel</Button>
        </Buttons>
      </BoxBody>
    );
  };

  render() {
    const { userIds, orgName, userName } = this.props;
    let title;
    if (userName) {
      title = (
        <span>
          Remove {userName} from {orgName}?
        </span>
      );
    } else {
      title = (
        <span>
          Remove {userIds.length === 1 ? '1 person' : `${userIds.length} people`} from {orgName}
        </span>
      );
    }

    return (
      <Dialog
        eventScreenName="RemoveFromOrgModal"
        size="small"
        onRequestHide={this.props.onRequestHide}
        title={title}>
        {this.renderBody()}
      </Dialog>
    );
  }
}

export default RemoveFromOrgModal;



// WEBPACK FOOTER //
// ./src/organizations/components/RemoveFromOrgModal.jsx